import { defaultText } from '@phaser/helpers/Text';
import { MessageEmitter } from '@phaser/events';
import { DEPTH } from '@phaser/data/depth';
import { Game } from '@phaser/scenes/Game';
import * as Shared from '@shared/lib';

const COMBO_WINDOW_MS = 1800;
const COMBO_THRESHOLD = 3;

export class ComboSystem {
  private _scene: Game = Game.current;
  private _combo = 0;
  private _lastShotTime = 0;
  private _breakTimer?: ReturnType<typeof setTimeout>;
  private _comboText?: Phaser.GameObjects.Text;
  private _multiplierText?: Phaser.GameObjects.Text;

  constructor() {
    Shared.MessageRegistry.emitter.on(Shared.PlayerShootMessage.Opcode, this._onShoot);
    MessageEmitter.on('comboBreak', this._onComboBreak);
  }

  public destroy() {
    Shared.MessageRegistry.emitter.off(Shared.PlayerShootMessage.Opcode, this._onShoot);
    MessageEmitter.off('comboBreak', this._onComboBreak);
    clearTimeout(this._breakTimer);
    this._comboText?.destroy();
    this._multiplierText?.destroy();
  }

  private _onShoot = (data: Shared.PlayerShootMessage) => {
    if (!this._scene.id.startsWith(data.playerId)) return;

    const now = Date.now();
    if (now - this._lastShotTime < COMBO_WINDOW_MS) {
      this._combo++;
    } else {
      this._combo = 1;
    }
    this._lastShotTime = now;

    clearTimeout(this._breakTimer);
    this._breakTimer = setTimeout(() => this._resetCombo(), COMBO_WINDOW_MS);

    if (this._combo >= COMBO_THRESHOLD) {
      this._showCombo();
    }
  };

  private _onComboBreak = () => {
    if (this._combo >= COMBO_THRESHOLD) {
      this._showBreak();
    }
    this._resetCombo();
  };

  private _resetCombo() {
    this._combo = 0;
    clearTimeout(this._breakTimer);
    this._comboText?.destroy();
    this._multiplierText?.destroy();
    this._comboText = undefined;
    this._multiplierText = undefined;
  }

  private _showCombo() {
    const camera = this._scene.cameras.main;
    const x = 8;
    const y = camera.displayHeight - 80;

    this._comboText?.destroy();
    this._multiplierText?.destroy();

    const COLORS = ['#ffffff', '#ffff00', '#ffaa00', '#ff5500', '#ff00ff', '#00ffff'];
    const colorIdx = Math.min(this._combo - COMBO_THRESHOLD, COLORS.length - 1);
    const color = COLORS[colorIdx];

    const baseFontSize = 13 + Math.min(this._combo - COMBO_THRESHOLD, 6) * 2;

    this._comboText = this._scene.add.text(x, y + 2, 'COMBO', {
      ...defaultText,
      color: '#aaaaaa',
      fontSize: '10px',
      strokeThickness: 1,
    })
      .setOrigin(0, 0.5)
      .setDepth(DEPTH.COMBO);

    this._multiplierText = this._scene.add.text(x, y - 10, `x${this._combo}`, {
      ...defaultText,
      color,
      fontSize: `${baseFontSize}px`,
    })
      .setOrigin(0, 0.5)
      .setDepth(DEPTH.COMBO);

    // Pop in animation
    this._scene.add.tween({
      targets: this._multiplierText,
      scale: { from: 1.5, to: 1 },
      ease: 'Back.out',
      duration: 120,
    });
  }

  private _showBreak() {
    const camera = this._scene.cameras.main;
    const x = 8;
    const y = camera.displayHeight - 80;

    const breakText = this._scene.add.text(x, y - 10, 'COMBO BREAK!', {
      ...defaultText,
      color: '#ff3333',
      fontSize: '12px',
    })
      .setOrigin(0, 0.5)
      .setDepth(DEPTH.COMBO);

    this._scene.add.tween({
      targets: breakText,
      y: y - 30,
      alpha: 0,
      duration: 700,
      ease: 'Power2',
      onComplete: () => breakText.destroy(),
    });
  }
}
